import React, {Component} from 'react';
import {action, observable, computed} from 'mobx';
import {inject, observer} from 'mobx-react';
import {Button, Card, CardBody, FormInput, Modal, ModalBody, ModalHeader, ModalFooter} from 'shards-react';
import FeatherIcon from 'feather-icons-react';
import {toast} from 'react-toastify';
import moment from 'moment';
import PageTitle from '../../../components/common/PageTitle';

// 보호 컬렉션 — 백엔드와 동일하게 유지 (UI 차단 + 사용자 안내용)
const PROTECTED_COLLECTIONS = new Set([
  'store',
  'withdrawal',
  'inAppOrder',
  'messageCharge',
  'adminAccessLog',
  'teamUser',
  'teamUser(selStore_id)',
]);

const DELETE_KEYWORD = 'DELETE';

@inject(({teamUser}) => ({isLogin: teamUser.isLogin, me: teamUser.me}))
@inject(({AOA}) => ({
  searchAllStoresForDataCleanup: AOA.searchAllStoresForDataCleanup,
  getStoreDataCounts: AOA.getStoreDataCounts,
  deleteStoreCollectionData: AOA.deleteStoreCollectionData,
}))
@observer
class StoreDataCounts extends Component {

  @observable keyword = '';
  @observable searching = false;
  @observable searchResults = []; // [{_id, name, platformId, email, createdAt, useExpiryDate}]

  @observable selectedStore = null; // 선택된 스토어 객체
  @observable loadingCounts = false;
  @observable counts = []; // [{collection, count}]
  @observable totalCount = 0;
  @observable storeName = '';

  // === 삭제 모드 / 확인 모달 ===
  @observable deleteMode = false;
  @observable selectedCollections = {}; // {collectionName: true}
  @observable deleteModalOpen = false;
  @observable confirmInput = '';
  @observable ackUnderstand = false;     // "데이터 손실 위험성 이해" 체크
  @observable ackIrreversible = false;   // "복구 불가 인지" 체크
  @observable deleting = false;
  @observable deleteResults = null;      // mutation 응답 결과

  isProtected = (col) => PROTECTED_COLLECTIONS.has(col);

  @computed get selectedCollectionList() {
    return Object.keys(this.selectedCollections).filter(k => this.selectedCollections[k]);
  }

  @computed get selectedCollectionCount() {
    return this.selectedCollectionList.length;
  }

  @computed get selectedTotal() {
    let sum = 0;
    const map = {};
    (this.counts || []).forEach(c => { map[c.collection] = c.count || 0; });
    this.selectedCollectionList.forEach(c => { sum += (map[c] || 0); });
    return sum;
  }

  @computed get confirmMatched() {
    const trimmed = (this.confirmInput || '').trim();
    if (!trimmed) return false;
    if (trimmed === DELETE_KEYWORD) return true;
    const name = (this.storeName || (this.selectedStore && this.selectedStore.name) || '').trim();
    return name.length > 0 && trimmed === name;
  }

  @computed get canExecuteDelete() {
    return this.selectedCollectionCount > 0
      && this.ackUnderstand
      && this.ackIrreversible
      && this.confirmMatched
      && !this.deleting;
  }

  componentDidMount() {
    // URL 파라미터로 직접 진입 지원
    const params = new URLSearchParams(window.location.search);
    const storeId = params.get('store_id');
    const kw = params.get('keyword');
    if (kw) {
      this.keyword = kw;
      this.search();
    }
    if (storeId) {
      // store 정보 없이도 카운트 조회 가능
      this.selectedStore = {_id: storeId, name: ''};
      this.loadCounts(storeId);
    }
  }

  @action onChangeKeyword = (e) => { this.keyword = e.target.value; };

  @action search = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const kw = (this.keyword || '').trim();
    if (!kw) {
      toast.error('검색어를 입력해 주세요.');
      return;
    }
    this.searching = true;
    this.searchResults = [];
    try {
      const res = await this.props.searchAllStoresForDataCleanup({
        keyword: kw,
        limit: 200
      });
      if (res && res.errors && res.errors.length > 0) {
        toast.error(res.errors[0].message || '검색 실패');
        return;
      }
      const data = (res && res.data) || res || {};
      if (data.error) {
        toast.error(data.error);
        return;
      }
      this.searchResults = data.docs || [];
      if (this.searchResults.length === 0) {
        toast.info('일치하는 스토어가 없습니다.');
      }
    } catch (err) {
      console.error(err);
      toast.error('검색 중 오류가 발생했습니다.');
    } finally {
      this.searching = false;
    }
  };

  @action selectStore = (store) => {
    // 다른 스토어로 전환 시 삭제 모드/선택 초기화 (안전)
    if (this.selectedStore && this.selectedStore._id !== store._id) {
      this.exitDeleteMode();
    }
    this.selectedStore = store;
    this.loadCounts(store._id);
    // URL 동기화
    const params = new URLSearchParams(window.location.search);
    params.set('store_id', store._id);
    window.history.replaceState(null, '', window.location.pathname + '?' + params.toString());
  };

  @action loadCounts = (storeId) => {
    if (!storeId) return;
    this.loadingCounts = true;
    this.counts = [];
    this.totalCount = 0;
    this.storeName = '';
    this.props.getStoreDataCounts({store_id: storeId}, (res) => {
      this.loadingCounts = false;
      if (res && res.errors && res.errors.length > 0) {
        toast.error(res.errors[0].message || '조회 실패');
        return;
      }
      if (res instanceof Error) {
        toast.error(res.message);
        return;
      }
      const data = (res && res.data) || res || {};
      this.counts = data.collections || [];
      this.totalCount = data.totalCount || 0;
      this.storeName = data.storeName || (this.selectedStore && this.selectedStore.name) || '';
    });
  };

  @action clearSelection = () => {
    this.selectedStore = null;
    this.counts = [];
    this.totalCount = 0;
    this.storeName = '';
    this.exitDeleteMode();
    const url = window.location.pathname + (this.keyword ? '?keyword=' + encodeURIComponent(this.keyword) : '');
    window.history.replaceState(null, '', url);
  };

  fmt = (n) => (n || 0).toLocaleString('ko-KR');

  // === 삭제 모드 / 확인 모달 ===
  @action enterDeleteMode = () => {
    if (!this.selectedStore || this.counts.length === 0) {
      toast.error('먼저 스토어를 선택하고 데이터를 조회해 주세요.');
      return;
    }
    this.deleteMode = true;
    this.selectedCollections = {};
    this.deleteResults = null;
  };

  @action exitDeleteMode = () => {
    this.deleteMode = false;
    this.selectedCollections = {};
    this.deleteModalOpen = false;
    this.confirmInput = '';
    this.ackUnderstand = false;
    this.ackIrreversible = false;
    this.deleting = false;
    this.deleteResults = null;
  };

  @action toggleCollectionSelection = (col) => {
    if (this.isProtected(col)) return;
    this.selectedCollections[col] = !this.selectedCollections[col];
    if (!this.selectedCollections[col]) delete this.selectedCollections[col];
    this.selectedCollections = {...this.selectedCollections};
  };

  @action openDeleteModal = () => {
    if (this.selectedCollectionCount === 0) {
      toast.error('삭제할 컬렉션을 선택해 주세요.');
      return;
    }
    this.confirmInput = '';
    this.ackUnderstand = false;
    this.ackIrreversible = false;
    this.deleteResults = null;
    this.deleteModalOpen = true;
  };

  @action closeDeleteModal = () => {
    if (this.deleting) return; // 진행 중에는 닫기 불가
    this.deleteModalOpen = false;
  };

  @action executeDelete = async () => {
    if (!this.canExecuteDelete) return;
    if (!this.selectedStore || !this.selectedStore._id) return;

    // 최종 확인 — 추가 한번 더 (이중 가드)
    if (!window.confirm(
      `마지막 확인입니다.\n\n[${this.storeName || this.selectedStore.name || '(이름 없음)'}] 스토어의 `
      + `${this.selectedCollectionCount}개 컬렉션에서 약 ${this.fmt(this.selectedTotal)}개 문서가 삭제됩니다.\n\n`
      + '이 작업은 되돌릴 수 없습니다. 진행하시겠습니까?'
    )) return;

    this.deleting = true;
    this.deleteResults = null;

    try {
      const res = await this.props.deleteStoreCollectionData({
        store_id: this.selectedStore._id,
        collections: this.selectedCollectionList,
        confirmText: this.confirmInput.trim(),
        expectedTotal: this.selectedTotal,
      });
      if (res && res.errors && res.errors.length > 0) {
        toast.error(res.errors[0].message || '삭제 실패');
        this.deleting = false;
        return;
      }
      const data = (res && res.data) || {};
      if (!data.ok) {
        toast.error(data.error || '삭제 실패');
        this.deleting = false;
        return;
      }
      this.deleteResults = data;
      const failed = (data.results || []).filter(r => !r.ok && r.error);
      if (failed.length > 0) {
        toast.warn(`완료: 총 ${this.fmt(data.totalDeleted)}건 삭제. 실패 ${failed.length}건 — 결과 확인 필요.`);
      } else {
        toast.success(`완료: 총 ${this.fmt(data.totalDeleted)}건 삭제됨.`);
      }
    } catch (err) {
      console.error(err);
      toast.error('삭제 처리 중 오류가 발생했습니다.');
    } finally {
      this.deleting = false;
      // 카운트 재조회
      if (this.selectedStore && this.selectedStore._id) {
        this.loadCounts(this.selectedStore._id);
      }
    }
  };

  render() {
    const results = this.searchResults || [];
    const counts = this.counts || [];

    return (
      <div style={{padding: '20px'}}>
        <PageTitle title="스토어 데이터 현황" subtitle="스토어 관리" className="mb-3"/>

        {/* 검색 영역 */}
        <Card style={{marginBottom: '16px'}}>
          <CardBody>
            <form onSubmit={this.search} style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
              <FormInput
                placeholder="스토어 이름 / PlatformId / 이메일 / 도메인 / 샵명 / store_id 검색 (샘플/탈퇴 포함 전체)"
                value={this.keyword}
                onChange={this.onChangeKeyword}
                style={{maxWidth: '480px'}}
              />
              <Button theme="primary" type="submit" disabled={this.searching}>
                <FeatherIcon icon="search" size={14}/> {this.searching ? '검색 중...' : '검색'}
              </Button>
              {this.selectedStore && (
                <Button theme="light" onClick={this.clearSelection}>
                  선택 해제
                </Button>
              )}
            </form>
          </CardBody>
        </Card>

        {/* 선택된 스토어의 데이터 카운트 */}
        {this.selectedStore && (
          <Card style={{marginBottom: '16px'}}>
            <CardBody>
              <div style={{marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px'}}>
                <div>
                  <div style={{fontSize: '18px', fontWeight: '600', color: '#333'}}>
                    {this.storeName || this.selectedStore.name || '(이름 없음)'}
                  </div>
                  <div style={{fontSize: '12px', color: '#6c757d', marginTop: '4px'}}>
                    <code style={{backgroundColor: '#f1f5f9', padding: '2px 6px', borderRadius: '4px'}}>
                      store_id: {this.selectedStore._id}
                    </code>
                    {this.selectedStore.platformId && (
                      <span style={{marginLeft: '8px'}}>platformId: <strong>{this.selectedStore.platformId}</strong></span>
                    )}
                  </div>
                </div>
                <div style={{textAlign: 'right'}}>
                  <div style={{fontSize: '11px', color: '#8898aa'}}>총 데이터 합계</div>
                  <div style={{fontSize: '24px', fontWeight: '700', color: '#5664D2'}}>{this.fmt(this.totalCount)}</div>
                </div>
              </div>

              {/* 삭제 모드 토글 / 액션 바 */}
              {counts.length > 0 && (
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginBottom: '10px', padding: '8px 12px',
                  backgroundColor: this.deleteMode ? '#FEF2F2' : '#F9FAFB',
                  border: this.deleteMode ? '1px solid #FCA5A5' : '1px solid #E5E7EB',
                  borderRadius: '6px',
                  gap: '12px', flexWrap: 'wrap'
                }}>
                  <div style={{fontSize: '12px', color: this.deleteMode ? '#991B1B' : '#6B7280', display: 'flex', alignItems: 'center', gap: '6px'}}>
                    <FeatherIcon icon={this.deleteMode ? 'alert-triangle' : 'shield'} size={14}/>
                    {this.deleteMode
                      ? <span><b>삭제 모드 활성</b> — 컬렉션을 선택하세요. (보호 컬렉션은 선택 불가)</span>
                      : <span>일반 모드 — 삭제하려면 우측 버튼을 눌러 삭제 모드를 활성화하세요.</span>
                    }
                  </div>
                  <div style={{display: 'flex', gap: '8px'}}>
                    {!this.deleteMode && (
                      <Button size="sm" theme="danger" outline onClick={this.enterDeleteMode}>
                        <FeatherIcon icon="trash-2" size={12}/> 삭제 모드 진입
                      </Button>
                    )}
                    {this.deleteMode && (
                      <>
                        <Button size="sm" theme="light" onClick={this.exitDeleteMode} disabled={this.deleting}>
                          취소
                        </Button>
                        <Button size="sm" theme="danger"
                                disabled={this.selectedCollectionCount === 0 || this.deleting}
                                onClick={this.openDeleteModal}>
                          <FeatherIcon icon="trash-2" size={12}/>
                          {' '}선택 {this.selectedCollectionCount}개 삭제 ({this.fmt(this.selectedTotal)}건)
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {this.loadingCounts ? (
                <div style={{textAlign: 'center', padding: '40px', color: '#adb5bd'}}>
                  <FeatherIcon icon="loader" size={20}/> 데이터 조회 중...
                </div>
              ) : counts.length === 0 ? (
                <div style={{textAlign: 'center', padding: '40px', color: '#adb5bd'}}>
                  데이터가 없습니다.
                </div>
              ) : (
                <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px'}}>
                  <thead>
                    <tr style={{backgroundColor: '#f8f9fc', borderBottom: '2px solid #e9ecef'}}>
                      {this.deleteMode && (
                        <th style={{padding: '10px 12px', textAlign: 'center', color: '#6c757d', fontWeight: '600', width: '44px'}}>선택</th>
                      )}
                      <th style={{padding: '10px 12px', textAlign: 'left', color: '#6c757d', fontWeight: '600', width: '60px'}}>#</th>
                      <th style={{padding: '10px 12px', textAlign: 'left', color: '#6c757d', fontWeight: '600'}}>컬렉션</th>
                      <th style={{padding: '10px 12px', textAlign: 'right', color: '#6c757d', fontWeight: '600', width: '140px'}}>건수</th>
                      <th style={{padding: '10px 12px', textAlign: 'left', color: '#6c757d', fontWeight: '600', width: '200px'}}>비율</th>
                    </tr>
                  </thead>
                  <tbody>
                    {counts.map((c, i) => {
                      const ratio = this.totalCount > 0 ? (c.count / this.totalCount * 100) : 0;
                      const isProtected = this.isProtected(c.collection);
                      const isChecked = !!this.selectedCollections[c.collection];
                      const rowBg = isChecked ? '#FEE2E2' : (i % 2 === 0 ? '#fff' : '#fafbfc');
                      return (
                        <tr key={c.collection} style={{borderBottom: '1px solid #f0f0f0', backgroundColor: rowBg}}>
                          {this.deleteMode && (
                            <td style={{padding: '10px 12px', textAlign: 'center'}}>
                              {isProtected ? (
                                <span title="보호 컬렉션 — 이 화면에서 삭제할 수 없습니다." style={{color: '#9CA3AF'}}>
                                  <FeatherIcon icon="lock" size={14}/>
                                </span>
                              ) : (
                                <input type="checkbox"
                                       checked={isChecked}
                                       disabled={this.deleting}
                                       onChange={() => this.toggleCollectionSelection(c.collection)}/>
                              )}
                            </td>
                          )}
                          <td style={{padding: '10px 12px', color: '#adb5bd'}}>{i + 1}</td>
                          <td style={{padding: '10px 12px', color: '#333', fontWeight: '500'}}>
                            <code style={{backgroundColor: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontSize: '12px'}}>{c.collection}</code>
                            {isProtected && this.deleteMode && (
                              <span style={{marginLeft: '6px', fontSize: '11px', color: '#9CA3AF'}}>보호됨</span>
                            )}
                          </td>
                          <td style={{padding: '10px 12px', textAlign: 'right', color: c.count > 0 ? '#16a34a' : '#adb5bd', fontWeight: '600'}}>
                            {this.fmt(c.count)}
                          </td>
                          <td style={{padding: '10px 12px'}}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                              <div style={{flex: 1, height: '6px', backgroundColor: '#e9ecef', borderRadius: '3px', overflow: 'hidden'}}>
                                <div style={{width: ratio + '%', height: '100%', backgroundColor: '#5664D2'}}/>
                              </div>
                              <span style={{fontSize: '11px', color: '#8898aa', minWidth: '40px', textAlign: 'right'}}>
                                {ratio.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardBody>
          </Card>
        )}

        {/* 검색 결과 */}
        {results.length > 0 && (
          <Card>
            <CardBody>
              <div style={{marginBottom: '12px', fontSize: '14px', color: '#6c757d'}}>
                검색 결과 <strong style={{color: '#333'}}>{results.length}</strong>건
              </div>
              <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px'}}>
                <thead>
                  <tr style={{backgroundColor: '#f8f9fc', borderBottom: '2px solid #e9ecef'}}>
                    <th style={{padding: '10px 12px', textAlign: 'left', color: '#6c757d', fontWeight: '600'}}>스토어명</th>
                    <th style={{padding: '10px 12px', textAlign: 'left', color: '#6c757d', fontWeight: '600'}}>PlatformId</th>
                    <th style={{padding: '10px 12px', textAlign: 'left', color: '#6c757d', fontWeight: '600'}}>대표자</th>
                    <th style={{padding: '10px 12px', textAlign: 'left', color: '#6c757d', fontWeight: '600'}}>이메일</th>
                    <th style={{padding: '10px 12px', textAlign: 'left', color: '#6c757d', fontWeight: '600'}}>가입일</th>
                    <th style={{padding: '10px 12px', textAlign: 'center', color: '#6c757d', fontWeight: '600', width: '120px'}}>액션</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((store, idx) => {
                    const isSelected = this.selectedStore && this.selectedStore._id === store._id;
                    const badges = [];
                    if (store.isSample === true) badges.push({label: '샘플', color: '#92400E', bg: '#FEF3C7'});
                    if (store.isDelApp === true) badges.push({label: '앱삭제', color: '#991B1B', bg: '#FEE2E2'});
                    if (store.isFreeStore === true) badges.push({label: '무료', color: '#1E40AF', bg: '#DBEAFE'});
                    if (store.manualStatus) badges.push({label: store.manualStatus, color: '#374151', bg: '#E5E7EB'});
                    return (
                      <tr key={store._id || idx} style={{
                        borderBottom: '1px solid #f0f0f0',
                        backgroundColor: isSelected ? '#eef2ff' : (idx % 2 === 0 ? '#fff' : '#fafbfc')
                      }}>
                        <td style={{padding: '10px 12px', fontWeight: '500', color: '#333'}}>
                          <div>{store.name || '-'}</div>
                          {badges.length > 0 && (
                            <div style={{display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap'}}>
                              {badges.map((b, i) => (
                                <span key={i} style={{
                                  fontSize: '10px', padding: '1px 6px', borderRadius: '4px',
                                  color: b.color, backgroundColor: b.bg, fontWeight: 600
                                }}>{b.label}</span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td style={{padding: '10px 12px', color: '#6c757d'}}>{store.platformId || '-'}</td>
                        <td style={{padding: '10px 12px', color: '#6c757d'}}>{store.presidentName || '-'}</td>
                        <td style={{padding: '10px 12px', color: '#6c757d'}}>{store.email || '-'}</td>
                        <td style={{padding: '10px 12px', color: '#adb5bd', whiteSpace: 'nowrap'}}>
                          {store.createdAt ? moment(store.createdAt).format('YYYY-MM-DD') : '-'}
                        </td>
                        <td style={{padding: '10px 12px', textAlign: 'center'}}>
                          <Button
                            size="sm"
                            theme={isSelected ? 'secondary' : 'primary'}
                            disabled={isSelected && this.loadingCounts}
                            onClick={() => this.selectStore(store)}
                            style={{fontSize: '11px', padding: '4px 10px'}}
                          >
                            {isSelected ? '조회됨' : '데이터 조회'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardBody>
          </Card>
        )}

        {/* 안내 */}
        {!this.selectedStore && results.length === 0 && (
          <Card>
            <CardBody>
              <div style={{textAlign: 'center', padding: '60px 20px', color: '#adb5bd'}}>
                <FeatherIcon icon="database" size={40} style={{marginBottom: '12px'}}/>
                <div style={{fontSize: '14px'}}>스토어를 검색하면 해당 스토어의 모든 컬렉션 데이터 현황을 조회할 수 있습니다.</div>
                <div style={{fontSize: '12px', marginTop: '8px'}}>
                  store_id가 있는 모든 컬렉션 + store / teamUser(selStore_id) / withdrawal 까지 포함
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        {/* 삭제 확인 모달 */}
        <Modal open={this.deleteModalOpen} toggle={this.closeDeleteModal} size="lg" backdrop="static">
          <ModalHeader toggle={this.deleting ? undefined : this.closeDeleteModal}>
            <span style={{color: '#B91C1C'}}>
              <FeatherIcon icon="alert-triangle" size={18}/> 컬렉션 데이터 일괄 삭제 — 최종 확인
            </span>
          </ModalHeader>
          <ModalBody>
            {!this.deleteResults && (
              <>
                <div style={{
                  padding: '12px', marginBottom: '12px',
                  backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: '6px',
                  color: '#991B1B', fontSize: '13px', lineHeight: 1.6
                }}>
                  <div style={{fontWeight: 700, marginBottom: '4px'}}>⚠ 이 작업은 되돌릴 수 없습니다.</div>
                  <div>대상 스토어: <b>{this.storeName || (this.selectedStore && this.selectedStore.name) || '(이름 없음)'}</b></div>
                  <div style={{fontSize: '11px', marginTop: '2px'}}>store_id: <code>{this.selectedStore && this.selectedStore._id}</code></div>
                </div>

                <div style={{marginBottom: '8px', fontSize: '13px', color: '#374151', fontWeight: 600}}>
                  삭제될 컬렉션 ({this.selectedCollectionCount}개)
                </div>
                <div style={{
                  maxHeight: '220px', overflowY: 'auto',
                  border: '1px solid #E5E7EB', borderRadius: '6px',
                  marginBottom: '12px'
                }}>
                  <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '12px'}}>
                    <thead>
                      <tr style={{backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB'}}>
                        <th style={{padding: '6px 10px', textAlign: 'left', color: '#6B7280'}}>컬렉션</th>
                        <th style={{padding: '6px 10px', textAlign: 'right', color: '#6B7280', width: '100px'}}>삭제 예정</th>
                      </tr>
                    </thead>
                    <tbody>
                      {this.selectedCollectionList.map(name => {
                        const found = this.counts.find(c => c.collection === name);
                        const cnt = found ? found.count : 0;
                        return (
                          <tr key={name} style={{borderBottom: '1px solid #F3F4F6'}}>
                            <td style={{padding: '6px 10px'}}>
                              <code style={{backgroundColor: '#FEE2E2', padding: '2px 6px', borderRadius: '4px', color: '#991B1B'}}>{name}</code>
                            </td>
                            <td style={{padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: '#B91C1C'}}>
                              {this.fmt(cnt)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{borderTop: '2px solid #E5E7EB', backgroundColor: '#FEF2F2'}}>
                        <td style={{padding: '8px 10px', fontWeight: 700}}>총 삭제 예정</td>
                        <td style={{padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#991B1B', fontSize: '14px'}}>
                          {this.fmt(this.selectedTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div style={{marginBottom: '10px'}}>
                  <label style={{display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#374151'}}>
                    <input type="checkbox"
                           checked={this.ackUnderstand}
                           disabled={this.deleting}
                           onChange={() => this.ackUnderstand = !this.ackUnderstand}
                           style={{marginTop: '3px'}}/>
                    <span>해당 데이터가 영구 삭제되어 복구할 수 없음을 이해했습니다.</span>
                  </label>
                </div>
                <div style={{marginBottom: '14px'}}>
                  <label style={{display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#374151'}}>
                    <input type="checkbox"
                           checked={this.ackIrreversible}
                           disabled={this.deleting}
                           onChange={() => this.ackIrreversible = !this.ackIrreversible}
                           style={{marginTop: '3px'}}/>
                    <span>별도의 백업/복구 수단이 없으며 본인이 이 결정에 책임을 진다는 점에 동의합니다.</span>
                  </label>
                </div>

                <div style={{marginBottom: '6px', fontSize: '13px', color: '#374151', fontWeight: 600}}>
                  확인 텍스트 입력
                </div>
                <div style={{marginBottom: '6px', fontSize: '12px', color: '#6B7280'}}>
                  다음 중 하나를 정확히 입력해 주세요:
                  &nbsp;<code style={{backgroundColor: '#F3F4F6', padding: '2px 6px', borderRadius: '4px'}}>{this.storeName || (this.selectedStore && this.selectedStore.name) || '(이름 없음)'}</code>
                  &nbsp;또는&nbsp;
                  <code style={{backgroundColor: '#F3F4F6', padding: '2px 6px', borderRadius: '4px'}}>{DELETE_KEYWORD}</code>
                </div>
                <FormInput
                  value={this.confirmInput}
                  disabled={this.deleting}
                  onChange={(e) => this.confirmInput = e.target.value}
                  placeholder="스토어 이름 또는 DELETE"
                  valid={this.confirmMatched ? true : undefined}
                  invalid={(this.confirmInput || '').length > 0 && !this.confirmMatched ? true : undefined}
                />
              </>
            )}

            {/* 삭제 결과 */}
            {this.deleteResults && (
              <>
                <div style={{
                  padding: '12px', marginBottom: '12px',
                  backgroundColor: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: '6px',
                  color: '#166534', fontSize: '13px'
                }}>
                  <div style={{fontWeight: 700, marginBottom: '4px'}}>✓ 처리 완료</div>
                  <div>총 <b>{this.fmt(this.deleteResults.totalDeleted)}</b>건의 문서가 삭제되었습니다.</div>
                </div>
                <div style={{
                  maxHeight: '300px', overflowY: 'auto',
                  border: '1px solid #E5E7EB', borderRadius: '6px'
                }}>
                  <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '12px'}}>
                    <thead>
                      <tr style={{backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB'}}>
                        <th style={{padding: '6px 10px', textAlign: 'left'}}>컬렉션</th>
                        <th style={{padding: '6px 10px', textAlign: 'right', width: '90px'}}>이전</th>
                        <th style={{padding: '6px 10px', textAlign: 'right', width: '90px'}}>삭제됨</th>
                        <th style={{padding: '6px 10px', textAlign: 'center', width: '60px'}}>상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(this.deleteResults.results || []).map(r => (
                        <tr key={r.collection} style={{borderBottom: '1px solid #F3F4F6'}}>
                          <td style={{padding: '6px 10px'}}>
                            <code style={{backgroundColor: '#F3F4F6', padding: '2px 6px', borderRadius: '4px'}}>{r.collection}</code>
                          </td>
                          <td style={{padding: '6px 10px', textAlign: 'right'}}>{this.fmt(r.before)}</td>
                          <td style={{padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: r.deletedCount > 0 ? '#16a34a' : '#9CA3AF'}}>
                            {this.fmt(r.deletedCount)}
                          </td>
                          <td style={{padding: '6px 10px', textAlign: 'center'}}>
                            {r.ok ? (
                              <span style={{color: '#16a34a'}} title={r.note || ''}>
                                <FeatherIcon icon="check-circle" size={14}/>
                              </span>
                            ) : (
                              <span style={{color: '#DC2626'}} title={r.error || ''}>
                                <FeatherIcon icon="x-circle" size={14}/>
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* 실패 메시지 모음 */}
                {(this.deleteResults.results || []).some(r => !r.ok && r.error) && (
                  <div style={{marginTop: '8px', fontSize: '11px', color: '#991B1B'}}>
                    실패 사유:
                    <ul style={{margin: '4px 0 0 18px', padding: 0}}>
                      {(this.deleteResults.results || []).filter(r => !r.ok && r.error).map(r => (
                        <li key={r.collection}>
                          <code>{r.collection}</code>: {r.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {!this.deleteResults && (
              <>
                <Button theme="light" onClick={this.closeDeleteModal} disabled={this.deleting}>취소</Button>
                <Button theme="danger" onClick={this.executeDelete} disabled={!this.canExecuteDelete}>
                  {this.deleting ? (
                    <><FeatherIcon icon="loader" size={12}/> 삭제 진행 중...</>
                  ) : (
                    <><FeatherIcon icon="trash-2" size={12}/> 삭제 실행</>
                  )}
                </Button>
              </>
            )}
            {this.deleteResults && (
              <Button theme="primary" onClick={() => { this.deleteModalOpen = false; this.exitDeleteMode(); }}>
                닫기
              </Button>
            )}
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}

export default StoreDataCounts;
